import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";

const STATE_TINTS = ["#2A1F1D", "#0E3B33", "#4A2130", "#3A2C10"];
const tintFor = (slug) => STATE_TINTS[[...slug].reduce((a, c) => a + c.charCodeAt(0), 0) % STATE_TINTS.length];

const isNigeria = (d) => d.slug.startsWith("state-") || d.slug === "lagos" || d.slug === "abuja";
const destHref = (d) =>
  d.slug.startsWith("state-") ? `/destinations/state/${d.slug.replace(/^state-/, "")}` : `/destinations/${d.slug}`;
const regionOf = (d) => (d.region || "").replace("Nigeria · ", "").trim() || "Nigeria";

const DestCard = ({ d, i }) => (
  <FadeUp delay={Math.min(i * 0.03, 0.3)}>
    <Link
      to={destHref(d)}
      data-testid={`destination-card-${d.slug}`}
      className="group relative block h-72 cursor-pointer overflow-hidden"
    >
      {d.image_url ? (
        <>
          <img
            src={d.image_url}
            alt={d.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/85 via-jawa-nearblack/20 to-transparent" />
        </>
      ) : (
        <div
          className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105"
          style={{ background: `linear-gradient(150deg, ${tintFor(d.slug)} 0%, #111111 130%)` }}
        >
          <span className="absolute -right-4 -top-10 select-none font-serif text-[11rem] font-medium leading-none text-jawa-ivory/[0.07]">
            {d.name[0]}
          </span>
          <span className="absolute bottom-3 right-4 text-[9px] uppercase tracking-wider text-jawa-ivory/40">
            Photo coming soon
          </span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
        <div>
          <h3 className="font-serif text-3xl font-medium tracking-tight text-jawa-ivory">{d.name}</h3>
          <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-jawa-ivory/70">{d.region} · {d.tagline}</p>
        </div>
        <MapPin className="h-5 w-5 shrink-0 text-jawa-gold transition-transform duration-300 group-hover:-translate-y-1" />
      </div>
    </Link>
  </FadeUp>
);

export default function DestinationsPage() {
  const [destinations, setDestinations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("All");

  useEffect(() => {
    api
      .get("/destinations")
      .then((res) => setDestinations(res.data))
      .catch(() => setDestinations([]))
      .finally(() => setLoaded(true));
  }, []);

  const states = destinations.filter(isNigeria);
  const beyond = destinations.filter((d) => !isNigeria(d) && d.slug !== "nigeria");
  const regions = useMemo(
    () => ["All", ...Array.from(new Set(states.map(regionOf))).sort()],
    [states]
  );

  const filtered = states.filter((d) => {
    const q = query.trim().toLowerCase();
    const matchesQ =
      !q ||
      d.name.toLowerCase().includes(q) ||
      (d.tagline || "").toLowerCase().includes(q) ||
      (d.overview || "").toLowerCase().includes(q);
    const matchesR = region === "All" || regionOf(d) === region;
    return matchesQ && matchesR;
  });

  return (
    <div className="bg-jawa-ivory" data-testid="destinations-page">
      <Nav />
      <Seo
        title="Destinations — Explore Nigeria & Africa | JÀWÀ Africa"
        description="Explore Nigeria's 36 states + FCT and curated African destinations with practical JÀWÀ guidance — where to go, what to do and how to arrive prepared."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="Explore Africa" title="Destinations" />
          <p className="mt-6 max-w-xl text-base leading-relaxed text-jawa-espresso/70">
            Contemporary Africa, state by state and city by city — all 36 Nigerian states, the Federal
            Capital Territory and a growing list of destinations beyond.
          </p>
        </FadeUp>

        <FadeUp className="mt-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-jawa-espresso/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a state, city or highlight…"
              data-testid="destinations-search"
              className="w-full border border-jawa-espresso/20 bg-white py-3 pl-11 pr-4 text-sm text-jawa-espresso outline-none transition-colors placeholder:text-jawa-espresso/40 focus:border-jawa-turquoise"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="destinations-region-filters">
            {regions.map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                data-testid={`region-filter-${r.toLowerCase().replace(/\s+/g, "-")}`}
                className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                  region === r
                    ? "border-jawa-espresso bg-jawa-espresso text-jawa-ivory"
                    : "border-jawa-espresso/20 text-jawa-espresso/60 hover:border-jawa-turquoise hover:text-jawa-turquoise"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </FadeUp>

        {!loaded && <p className="mt-16 text-sm uppercase tracking-[0.25em] text-jawa-espresso/50">Loading…</p>}

        <FadeUp className="mt-12">
          <h2 className="font-serif text-2xl font-medium tracking-tight text-jawa-espresso" data-testid="nigeria-heading">
            Nigeria — 36 states + the Federal Capital Territory
          </h2>
          <p className="mt-2 text-xs uppercase tracking-[0.25em] text-jawa-turquoise">
            {filtered.length} of {states.length} shown
          </p>
        </FadeUp>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="nigeria-grid">
          {filtered.map((d, i) => (
            <DestCard key={d.slug} d={d} i={i} />
          ))}
        </div>
        {loaded && filtered.length === 0 && (
          <p className="mt-8 text-sm text-jawa-espresso/60" data-testid="destinations-no-results">
            No destinations match that search — try a state name, a region or a highlight like “waterfall”.
          </p>
        )}

        {beyond.length > 0 && (
          <>
            <FadeUp className="mt-20">
              <h2 className="font-serif text-2xl font-medium tracking-tight text-jawa-espresso" data-testid="beyond-heading">
                Beyond Nigeria
              </h2>
            </FadeUp>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="beyond-grid">
              {beyond.map((d, i) => (
                <DestCard key={d.slug} d={d} i={i} />
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
